import type {
  ErrorEnvelope,
  InsufficientCurrencyEnvelope,
  InsufficientCurrencyExtra,
  RealEstateQuotaExceededEnvelope,
  RealEstateQuotaExceededExtra,
  RealEstateSoldOutEnvelope,
  RealEstateSoldOutExtra,
} from "../../client/types.gen";
import { notificationOccurred } from "../telegram/hapticFeedback";

type BusinessErrorType =
  | NonNullable<InsufficientCurrencyEnvelope["error_type"]>
  | NonNullable<RealEstateQuotaExceededEnvelope["error_type"]>
  | NonNullable<RealEstateSoldOutEnvelope["error_type"]>;

/**
 * Generic error_type values share ErrorEnvelope (OpenAPI: error_type is plain string).
 * Must stay aligned with backend ErrorCode for exhaustive handleApiError switch.
 */
const GENERIC_ERROR_TYPES = [
  "http_error",
  "not_found",
  "forbidden",
  "bad_request",
  "conflict",
] as const satisfies ReadonlyArray<ErrorEnvelope["error_type"]>;

type GenericErrorType = (typeof GENERIC_ERROR_TYPES)[number];

export type ErrorType = BusinessErrorType | GenericErrorType;

export type InsufficientCurrencyApiError = {
  errorType: NonNullable<InsufficientCurrencyEnvelope["error_type"]>;
  detail: string;
  status: number;
  extra: InsufficientCurrencyExtra;
};

export type RealEstateQuotaExceededApiError = {
  errorType: NonNullable<RealEstateQuotaExceededEnvelope["error_type"]>;
  detail: string;
  status: number;
  extra: RealEstateQuotaExceededExtra;
};

export type RealEstateSoldOutApiError = {
  errorType: NonNullable<RealEstateSoldOutEnvelope["error_type"]>;
  detail: string;
  status: number;
  extra: RealEstateSoldOutExtra;
};

export type GenericApiError = {
  errorType: GenericErrorType;
  detail: string;
  status: number;
  extra?: ErrorEnvelope["extra"];
};

export type ApiError =
  | InsufficientCurrencyApiError
  | RealEstateQuotaExceededApiError
  | RealEstateSoldOutApiError
  | GenericApiError;

const ERROR_TYPE_SET = new Set<string>([
  "insufficient_currency",
  "real_estate_quota_exceeded",
  "real_estate_sold_out",
  ...GENERIC_ERROR_TYPES,
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isApiEnvelope(
  body: unknown,
): body is {
  error_type: string;
  detail: string;
  status?: number;
  extra?: unknown;
} {
  return (
    isRecord(body) &&
    typeof body.error_type === "string" &&
    typeof body.detail === "string"
  );
}

function normalizeErrorType(raw: string): ErrorType {
  if (ERROR_TYPE_SET.has(raw)) {
    return raw as ErrorType;
  }
  return "http_error";
}

export function parseApiError(status: number, body: unknown): ApiError {
  if (!isApiEnvelope(body)) {
    return {
      errorType: "http_error",
      detail: String(body),
      status,
    };
  }

  const errorType = normalizeErrorType(body.error_type);
  const detail = body.detail;
  const resolvedStatus =
    typeof body.status === "number" ? body.status : status;

  switch (errorType) {
    case "insufficient_currency":
      return {
        errorType,
        detail,
        status: resolvedStatus,
        extra: (isRecord(body.extra) ? body.extra : {}) as InsufficientCurrencyExtra,
      };
    case "real_estate_quota_exceeded":
      return {
        errorType,
        detail,
        status: resolvedStatus,
        extra: (isRecord(body.extra) ? body.extra : {}) as RealEstateQuotaExceededExtra,
      };
    case "real_estate_sold_out":
      return {
        errorType,
        detail,
        status: resolvedStatus,
        extra: (isRecord(body.extra) ? body.extra : {}) as RealEstateSoldOutExtra,
      };
    default:
      return {
        errorType,
        detail,
        status: resolvedStatus,
        extra: isRecord(body.extra) ? body.extra : undefined,
      };
  }
}

export class ApiErrorException extends Error {
  readonly apiError: ApiError;
  readonly originalCause?: unknown;

  constructor(apiError: ApiError, cause?: unknown) {
    super(apiError.detail);
    this.name = "ApiErrorException";
    this.apiError = apiError;
    this.originalCause = cause;
  }
}

export function isApiErrorException(error: unknown): error is ApiErrorException {
  return error instanceof ApiErrorException;
}

export function assertNever(value: never, message?: string): never {
  throw new Error(message ?? `Unexpected value: ${JSON.stringify(value)}`);
}

export function toApiError(error: unknown): ApiError {
  if (isApiErrorException(error)) {
    return error.apiError;
  }

  if (isApiEnvelope(error)) {
    return parseApiError(
      typeof error.status === "number" ? error.status : 0,
      error,
    );
  }

  if (error instanceof Error) {
    return parseApiError(0, error.message);
  }

  return parseApiError(0, error);
}

// ---------------------------------------------------------------------------
// Handler + React Query helpers (merged from handleApiError.ts / errorUtils.ts)
// ---------------------------------------------------------------------------

export interface HandleApiErrorContext {
  /** Label for console.error (e.g. component or action name). */
  logLabel?: string;
  /** Trigger error haptic feedback (default: true for generic errors). */
  haptic?: boolean;
}

/**
 * Normalizes thrown API errors and applies side effects only for generic (non-business) errors.
 * Business errors (insufficient_currency, quota, sold_out) are returned as-is for per-component modals.
 */
export function handleApiError(
  err: unknown,
  ctx?: HandleApiErrorContext,
): ApiError {
  const apiError = toApiError(err);
  const { errorType } = apiError;

  switch (errorType) {
    case "insufficient_currency":
    case "real_estate_quota_exceeded":
    case "real_estate_sold_out":
      return apiError;

    case "http_error":
    case "not_found":
    case "forbidden":
    case "bad_request":
    case "conflict": {
      console.error(ctx?.logLabel ?? "[API error]", apiError);
      if (ctx?.haptic !== false) {
        notificationOccurred("error");
      }
      return apiError;
    }

    default:
      return assertNever(errorType);
  }
}

/**
 * Checks whether an error is a server (5xx) failure.
 * Parses status from messages like `Request failed: 500` / `Request failed after N attempts: 503`.
 */
export function isServerError(error: unknown): boolean {
  if (!error) return false;

  const message =
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    (error as { message: unknown }).message
      ? String((error as { message: unknown }).message)
      : String(error || "");

  const statusMatch = message.match(
    /Request failed(?: after \d+ attempts)?: (\d{3})/,
  );

  if (statusMatch) {
    const status = parseInt(statusMatch[1], 10);
    return status >= 500 && status < 600;
  }

  return false;
}

/**
 * React Query `retry` callback that never retries 5xx server errors.
 * (Built-in react-query retry is only a counter — it does not distinguish 5xx.)
 */
export function createRetryFunction(maxRetries: number = 1) {
  return (failureCount: number, error: unknown) => {
    if (isServerError(error)) {
      return false; // do not retry server errors
    }
    return failureCount < maxRetries;
  };
}

/**
 * Minimal query shape for createRefetchIntervalFunction.
 * `error: Error | null` (not `unknown`) so useQuery still infers TError as Error.
 */
export type RefetchIntervalQuery = {
  state: { error: Error | null };
};

/**
 * React Query `refetchInterval` callback that stops polling on 5xx (and optional condition).
 */
export function createRefetchIntervalFunction(
  intervalMs: number,
  condition?: () => boolean,
) {
  return (query: RefetchIntervalQuery): number | false => {
    if (condition && !condition()) {
      return false;
    }

    const error = query.state.error;
    if (error && isServerError(error)) {
      return false; // stop refetch on server errors
    }
    return intervalMs;
  };
}
